import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./TeaserStat.scss";

import Button from "../../ui/Button/Button";
import Select from "../../ui/Select/Select";
import DateRange from "../../ui/DateRange/DateRange";
import ButtonGroup from "../../ui/ButtonGroup/ButtonGroup";

import { FixedHeader, PrimaryHeader, SecondaryHeader } from "../../ui/Header";

import TeaserStatTable from "./TeaserStatTable";
import LegendItem from "./LegendItem";
import TeaserStatChart from "./TeaserStatChart";
import * as urls from "../../utils/urls";

import withQs from "../../utils/WithQs";

import { detalization } from "../../constants";

import moment from "moment";

import { TeaserStatService } from "../../utils/api";

const chartTypes = {
  common: "common",
  inf: "inf",
  top: "top"
};

const detalization2name = {
  [detalization.day]: "День",
  [detalization.week]: "Неделя",
  [detalization.month]: "Месяц",
  [detalization.quarter]: "Четверть",
  [detalization.year]: "Год"
};

const detalization2format = {
  [detalization.day]: date => moment(date).format("YYYY-MM-DD"),
  [detalization.week]: date => moment(date).format("YYYY-MM-DD"),
  [detalization.month]: date => moment(date).format("YYYY-MM"),
  [detalization.quarter]: date => moment(date).format("YYYY-Q"),
  [detalization.year]: date => moment(date).format("YYYY")
};

const getAvailableDetalizations = (startDate, endDate) => {
  const duration = moment.duration(endDate.diff(startDate));
  const days = duration.as("days");

  if (days > 730) {
    return [detalization.quarter, detalization.year];
  } else if (days > 365) {
    return [detalization.month, detalization.quarter, detalization.year];
  } else if (days > 90) {
    return [
      detalization.week,
      detalization.month,
      detalization.quarter,
      detalization.year
    ];
  }

  return [
    detalization.day,
    detalization.week,
    detalization.month,
    detalization.quarter,
    detalization.year
  ];
};

const getDetalization = (detalization, availableDetalizations) => {
  return availableDetalizations.includes(detalization)
    ? detalization
    : availableDetalizations[0];
};

const getDetalizationOptions = availableDetalizations => {
  return availableDetalizations.map(detail => {
    return {
      label: detalization2name[detail],
      value: detail
    };
  });
};

const getChartType = search => {
  return search.chart_type || chartTypes.inf;
};

const getPeriod = search => {
  let startDate, endDate;

  if (search.date_from && search.date_to) {
    startDate = moment(search.date_from);
    endDate = moment(search.date_to);
  } else {
    endDate = moment();
    startDate = moment().subtract(30, "days");
  }

  return [startDate, endDate];
};

const TeaserStat = ({
  light,
  isModal,
  match,
  search,
  replaceSearch: setSearchOrig
}) => {
  const tid = match.params.id;
  const [startDate, endDate] = getPeriod(search);
  const startDateStr = startDate.format("YYYY-MM-DD");
  const endDateStr = endDate.format("YYYY-MM-DD");

  const availableDetalizations = getAvailableDetalizations(startDate, endDate);
  const detalizationOptions = getDetalizationOptions(availableDetalizations);
  const detalization = getDetalization(
    search.detalization,
    availableDetalizations
  );
  const dateFormatFunc = detalization2format[detalization];
  const chartType = getChartType(search);

  // state
  const [stat, setStat] = useState({ stat: [], summary: {} });
  const [loading, setLoading] = useState(false);

  // load
  useEffect(() => {
    setLoading(true);

    const params = {
      tid,
      detail: detalization,
      date_from: startDateStr,
      date_to: endDateStr
    };

    TeaserStatService(params)
      .then(data => {
        setLoading(false);
        setStat(data);
      })
      .catch(e => {
        console.log(e);
      });
  }, [tid, startDateStr, endDateStr, detalization]);

  // handlers

  const setSearch = params => setSearchOrig(params, { modal: isModal });

  const onChartTypeChange = chart_type => {
    setSearch({ chart_type });
  };

  const onDetalizationChange = item => {
    setSearch({ detalization: item.value });
  };

  const onPeriodChange = ({ startDate, endDate }) => {
    setSearch({
      date_from: moment(startDate).format("YYYY-MM-DD"),
      date_to: moment(endDate).format("YYYY-MM-DD")
    });
  };

  return (
    <div className="teaser-stat">
      <FixedHeader className="teaser-stat__header">
        <PrimaryHeader>
          <span className="header__title">Статистика анонса</span>
          <Select
            placeholder="Детализация"
            theme={light ? "" : "dark"}
            className="detalization"
            options={detalizationOptions}
            value={detalization}
            onSelect={onDetalizationChange}
          />
          <DateRange
            startDate={startDate}
            endDate={endDate}
            onChange={onPeriodChange}
            theme={light ? "" : "dark"}
          />

          <Link
            to={{
              pathname: urls.teaserList(),
              search: `teasers_search_term=[${tid}]`
            }}
          >
            <Button color="blue">Открыть</Button>
          </Link>

          <Link to={{ pathname: urls.teaserEdit(tid), state: { modal: true } }}>
            <Button color="blue">Редактировать</Button>
          </Link>
        </PrimaryHeader>
      </FixedHeader>
      <div className="content-body teaser-stat__content-body">
        <div className="teaser-stat__controls">
          <ButtonGroup
            items={[
              { title: "Общий", code: chartTypes.common },
              { title: "По виджетам", code: chartTypes.inf },
              { title: "По топу", code: chartTypes.top }
            ]}
            selected={chartType}
            onClick={onChartTypeChange}
          />

          <div className="teaser-stat__legend">
            <LegendItem className="clicks" color="#FF8B4F">
              Клики
            </LegendItem>
            <LegendItem className="clicks" color="#FFDFD8">
              Показы
            </LegendItem>
            <LegendItem className="clicks" color="#FE4D4B">
              CTR
            </LegendItem>
          </div>
        </div>
        <div className="teaser-stat__chart">
          <TeaserStatChart
            stat={stat.stat}
            chart={chartType}
            dateFormatFunc={dateFormatFunc}
          />
        </div>
        <TeaserStatTable
          loading={loading}
          stat={stat.stat}
          summary={stat.summary}
          dateFormatFunc={dateFormatFunc}
        />
      </div>
    </div>
  );
};

export default withQs(TeaserStat, "stat_");
